import {
  Product,
  ProductCalculations,
  ProductWithCalculations,
  StatusType,
  StatusConfig,
  GlobalSettings
} from '../types/inventory';

export const calculateInventoryMetrics = (
  product: Product,
  settings: GlobalSettings
): ProductCalculations => {
  const cmd = product.vendasPeriodo / product.diasPeriodo;
  const ddlt = cmd * settings.leadTime;
  const ss = cmd * settings.leadTime;
  const rop = ddlt + ss;
  const cobertura = cmd > 0 ? product.estoqueAtual / cmd : 0;
  const estoqueAlvo = ddlt + ss + (settings.diasCoberturaAlvo * cmd);
  const pedidoSugerido = Math.max(0, estoqueAlvo - product.estoqueAtual);

  return {
    cmd,
    ddlt,
    ss,
    rop,
    cobertura,
    estoqueAlvo,
    pedidoSugerido,
  };
};

export const determineProductStatus = (
  product: Product,
  calculations: ProductCalculations,
  settings: GlobalSettings
): StatusType => {
  if (product.estoqueAtual === 0) return 'ruptura';
  if (product.estoqueAtual < calculations.ss) return 'iminente';
  if (product.estoqueAtual >= calculations.ss && product.estoqueAtual <= calculations.rop) return 'minimo';
  if (calculations.cobertura < settings.leadTime) return 'critica';
  if (calculations.cobertura > settings.diasCoberturaAlvo) return 'excesso';
  return 'saudavel';
};

const statusMap: Record<StatusType, StatusConfig & { priority: number }> = {
  ruptura: {
    label: 'Ruptura Instalada',
    color: 'text-red-900',
    bgColor: 'bg-red-100',
    priority: 1,
  },
  iminente: {
    label: 'Ruptura Iminente',
    color: 'text-orange-900',
    bgColor: 'bg-orange-100',
    priority: 2,
  },
  minimo: {
    label: 'Perto do Mínimo',
    color: 'text-yellow-900',
    bgColor: 'bg-yellow-100',
    priority: 3,
  },
  critica: {
    label: 'Cobertura Crítica',
    color: 'text-cyan-900',
    bgColor: 'bg-cyan-100',
    priority: 4,
  },
  excesso: {
    label: 'Excesso',
    color: 'text-blue-900',
    bgColor: 'bg-blue-100',
    priority: 5,
  },
  saudavel: {
    label: 'Saudável',
    color: 'text-green-900',
    bgColor: 'bg-green-100',
    priority: 6,
  },
};

export const getStatusInfo = (status: StatusType): StatusConfig & { priority: number } => {
  return statusMap[status];
};

export const processProduct = (
  product: Product,
  settings: GlobalSettings
): ProductWithCalculations => {
  const calculations = calculateInventoryMetrics(product, settings);
  const status = determineProductStatus(product, calculations, settings);
  const info = getStatusInfo(status);

  return {
    ...product,
    ...calculations,
    status,
    statusLabel: info.label,
  };
};

export const processProducts = (
  products: Product[],
  settings: GlobalSettings
): ProductWithCalculations[] => {
  return products.map((product) => processProduct(product, settings));
};
